/**
 * 通用加密工具模块
 * 
 * ⚠️ 重要说明：
 * 1. 此工具仅用于 非axios 的场景，axios已有独立的加密实现
 * 2. 使用 encryptParams 方法会自动添加 _encrypted_fields 标识，符合后端约定
 * 3. 单独使用 getEncryptedUid 等便捷方法时，需手动处理 _encrypted_fields 标识
 * 
 * 推荐使用方式：
 * ```javascript
 * // ✅ 推荐：使用 encryptParams 自动处理标识
 * const secureParams = encryptParams({
 *   uid: userInfo.userid,
 *   username: userInfo.username,
 *   otherData: 'normal data'
 * });
 * 
 * // ❌ 不推荐：单独加密字段，缺少 _encrypted_fields 标识
 * const uid = getEncryptedUid(userInfo.userid);
 * ```
 */
import JSEncrypt from 'jsencrypt';

// 需要加密的参数名列表
const ENCRYPT_PARAMS = [
  'password',
  'username',
  'uid',
  'userid',
  'email',
  'phoneNum'
];

// RSA公钥 
const RSA_PUBLIC_KEY = `-----BEGIN PUBLIC KEY-----
MIGfMA0GCSqGSIb3DQEBAQUAA4GNADCBiQKBgQCMgrwaFraNYXHqN+XUOWFhgb48
skjkXPbMZqVS7r85GuFQmj91DDh1fjDUNU1KV+wNv4RxrBdLVE485OgAy5RDCMu9
9VA3FvxvNdr1eJR3PlRvcjyvPrvfnA3V8sReUCRB9KO4pb6b02IFI1qP5kD/VLE9
spfpT2J0hR8BAjAxMQIDAQAB
-----END PUBLIC KEY-----`;

// 过期时间戳(时长)
const EXPIRY_DURATION_MS = 10 * 60 * 1000; // 10分钟

/**
 * 创建RSA加密实例
 * @returns {JSEncrypt} 加密实例
 */
function createEncryptInstance () {
  const encrypt = new JSEncrypt();
  encrypt.setPublicKey(RSA_PUBLIC_KEY);
  return encrypt;
}

/**
 * 判断参数是否需要加密
 * @param {string} paramName - 参数名
 * @returns {boolean} 是否需要加密
 */
function shouldEncrypt (paramName) {
  return ENCRYPT_PARAMS.includes(paramName);
}

/**
 * 加密单个值
 * @param {any} value - 需要加密的值
 * @returns {string|null} 加密后的值，失败返回null
 */
function encryptValue (value) {
  if (value === undefined || value === null || value === '') {
    return value;
  }
  
  const encrypt = createEncryptInstance();
  return encrypt.encrypt(String(value));
}

/**
 * 加密单个参数（如果需要加密的话）
 * @param {string} paramName - 参数名
 * @param {any} value - 参数值
 * @returns {any} 加密后的值或原值
 */
function encryptParam (paramName, value) {
  if (!shouldEncrypt(paramName)) {
    return value;
  }
  
  const encrypted = encryptValue(value);
  return encrypted !== null ? encrypted : value;
}

/**
 * 批量加密对象的参数
 * @param {object} data - 需要加密的数据对象
 * @returns {object} 加密后的数据对象（包含加密标识）
 */
function encryptParams (data) {
  if (!data || typeof data !== 'object') {
    return data;
  }
  
  const encrypt = createEncryptInstance();
  const result = { ...data };
  const encryptedFields = [];
  
  for (const key of ENCRYPT_PARAMS) {
    if (result[key] !== undefined && result[key] !== null && result[key] !== '') {
      // 先给需要加密的数据加上有效时间戳
      let willEncryptString = (Date.now() + EXPIRY_DURATION_MS) + '_t_' + String(result[key]);
      const encrypted = encrypt.encrypt(willEncryptString);
      if (encrypted) {
        result[key] = encrypted;
        encryptedFields.push(key);
      }
    }
  }
  
  // 如果有字段被加密，添加加密标识告诉后端
  if (encryptedFields.length > 0) {
    result._encrypted_fields = encryptedFields;
  }
  
  return result;
}

/**
 * 安全获取用户ID（自动加密）
 * ⚠️ 警告：此方法只返回加密值，不包含 _encrypted_fields 标识
 * 建议使用 encryptParams 方法处理完整参数对象
 * @param {any} userid - 用户ID原值
 * @returns {string} 加密后的用户ID
 */
function getEncryptedUserId (userid) {
  if (!userid) {
    return '';
  }
  return encryptParam('userid', userid);
}

/**
 * 安全获取UID（自动加密）
 * ⚠️ 警告：此方法只返回加密值，不包含 _encrypted_fields 标识
 * 建议使用 encryptParams 方法处理完整参数对象
 * @param {any} uid - UID原值
 * @returns {string} 加密后的UID
 */
function getEncryptedUid (uid) {
  if (!uid) {
    return '';
  }
  return encryptParam('uid', uid);
}

/**
 * 创建安全的请求参数对象
 * @param {object} params - 原始参数对象
 * @returns {object} 加密后的参数对象
 */
function createSecureParams (params) {
  return encryptParams(params);
}

export {
  // 核心加密方法
  encryptValue,
  encryptParam,
  encryptParams,
  
  // 便捷方法
  getEncryptedUserId,
  getEncryptedUid,
  createSecureParams,
  
  // 工具方法
  shouldEncrypt,
  
  // 常量
  ENCRYPT_PARAMS,
  RSA_PUBLIC_KEY
};

export default {
  encryptValue,
  encryptParam,
  encryptParams,
  getEncryptedUserId,
  getEncryptedUid,
  createSecureParams,
  shouldEncrypt,
  ENCRYPT_PARAMS,
  RSA_PUBLIC_KEY
};
